import React from 'react';
import clsx from 'clsx';
import { useIntl, type MessageDescriptor } from 'react-intl';
import type { AssetStyleId } from 'fym-shared/src/music24/assetStyles';
import type { KitShape, StudioMoment } from 'fym-shared/src/music24/studioThemes';
import { TypedMemo } from '../../../../../common/types';
import { useIsNarrowScreen } from '../../../work/useIsNarrowScreen';
import type { MomentOption } from '../../studioRules';
import { studioMessages, studioMomentMessages } from '../../studioMessages';
import { StoryPreview } from '../../frames/StoryPreview';
import { studioStoryStyle } from '../../frames/studioStory';
import { DetailField } from '../DetailField';
import { PickTile, type PickTileOff } from './PickTile';
import { storyOfMoment, type StoryPreviewContext } from './storyPreviewContext';
import { imageTileMessages } from './imageTileMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the image tiles; each part uses its own slice of it */
import styles from './ImageTiles.module.scss';
/* eslint-enable css-modules/no-unused-class */

const STORY_BENEFITS: Record<StudioMoment, MessageDescriptor> = {
  preSave: imageTileMessages.preSaveBenefit,
  outNow: imageTileMessages.outNowBenefit,
  lyricLine: imageTileMessages.lyricBenefit,
  announce: imageTileMessages.countdownBenefit,
};

const FEED_BENEFITS: Record<StudioMoment, MessageDescriptor> = {
  ...STORY_BENEFITS,
  announce: imageTileMessages.announceBenefit,
};

interface MomentTilesProps {
  assetTypeId: 'story' | 'feedPost';
  options: MomentOption[];
  value?: StudioMoment;
  /** The style of the step: the mini Story files use it. */
  assetStyle: AssetStyleId | null;
  /** The shape of a feed post: the mini posts are 4:5 or square. */
  shape?: KitShape;
  context: StoryPreviewContext;
  /** "Add the lyrics": the Text view of the Lyrics section of the song page. */
  onAddLyrics: () => void;
  /** "Change the date": the release date on the song page. */
  onChangeDate: () => void;
  onChange: (moment: StudioMoment) => void;
}

/**
 * "Moment" of step 3 for a Story and a feed post (design studio-details a1, a2, a5.2): each moment
 * is a tile with a mini file that shows the hero of the moment. On a Story the moment `announce` is
 * "Countdown" (3 files: 7, 3 and 1 days). A moment that is off stays, dim, and says why.
 */
const MomentTilesComponent: React.FC<MomentTilesProps> = ({
  assetTypeId,
  options,
  value,
  assetStyle,
  shape,
  context,
  onAddLyrics,
  onChangeDate,
  onChange,
}) => {
  const intl = useIntl();
  const narrow = useIsNarrowScreen();
  const isStory = assetTypeId === 'story';
  const title = intl.formatMessage(studioMessages.momentTitle);

  const nameOf = (moment: StudioMoment): string =>
    isStory && moment === 'announce'
      ? intl.formatMessage(imageTileMessages.momentCountdown)
      : intl.formatMessage(studioMomentMessages[moment]);

  const offOf = (option: MomentOption): PickTileOff | undefined => {
    if (!option.disabled) {
      return undefined;
    }
    if (option.moment === 'lyricLine') {
      return {
        text: intl.formatMessage(imageTileMessages.offNoLyrics, { title: context.title }),
        action: { label: intl.formatMessage(imageTileMessages.offAddLyrics), onClick: onAddLyrics },
      };
    }
    return {
      text: intl.formatMessage(imageTileMessages.offTooNear),
      action: { label: intl.formatMessage(imageTileMessages.offChangeDate), onClick: onChangeDate },
    };
  };

  // A feed post is the Story stack in its 4:5 or square frame (design `asset-redesign/e`): its mini
  // files are the real layout too, not a theme still.
  const previewOf = (moment: StudioMoment): React.ReactNode => (
    <StoryPreview
      story={storyOfMoment(context, moment, undefined, assetTypeId, shape)}
      look={context.look}
      palette={context.palette}
      storyStyle={studioStoryStyle(assetStyle)}
      artworkUrl={context.artworkUrl}
      logoUrl={context.logoUrl}
      width={narrow ? 72 : 96}
    />
  );

  return (
    <DetailField
      title={title}
      hint={intl.formatMessage(isStory ? imageTileMessages.momentHintStory : imageTileMessages.momentHintFeed)}
    >
      <div
        className={clsx(styles.tiles, options.length >= 4 && styles.four, options.length <= 2 && styles.two)}
        role="radiogroup"
        aria-label={title}
      >
        {options.map((option) => (
          <PickTile
            key={option.moment}
            small
            testId={`moment-${option.moment}`}
            name={nameOf(option.moment)}
            isOn={option.moment === value}
            preview={previewOf(option.moment)}
            benefit={intl.formatMessage((isStory ? STORY_BENEFITS : FEED_BENEFITS)[option.moment])}
            files={
              isStory && option.moment === 'announce' ? intl.formatMessage(imageTileMessages.countdownFiles) : undefined
            }
            off={offOf(option)}
            onPick={() => onChange(option.moment)}
          />
        ))}
      </div>
    </DetailField>
  );
};

export const MomentTiles = TypedMemo(MomentTilesComponent);
